import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, Download, GitMerge, Plus, Upload, X } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import {
  PageHeaderActionPrimary,
  PageHeaderActionSecondary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { PageLineTabs } from '@/components/navigation/page-line-tabs'
import { TabCountBadge } from '@/components/navigation/tab-count-badge'
import { ListBrowseToolbar } from '@/components/filters/list-browse-toolbar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ListCard } from '@/components/list-card'
import { SkillNameComposer } from '@/components/skills/skill-name-composer'
import { getPageHeader } from '@/constants'
import { AGENT_HIGHLIGHT_PARAM } from '@/agent-highlight'
import {
  useAddDomainMembership,
  useAddSkillAlias,
  useApproveSkill,
  useApplyLexiconProposal,
  useCatalogExport,
  useCatalogImportApply,
  useCatalogImportPreview,
  useCreateApprovedSkill,
  useCreateSkillDomain,
  useDismissLexiconProposal,
  useLexiconProposals,
  useMergeSkills,
  useRemoveSkillAlias,
  useSetSkillPublicationStatus,
  useSkillDomain,
  useSkillDomains,
  type PublicationStatus,
  type SkillDomainSkill,
  type SkillDomainSummary,
  type SkillPoolRollup,
} from '@/hooks/use-evaluation-rubrics-admin'
import { useSkills, type Skill } from '@/hooks/use-skills'
import { domainIdForHighlight } from '@/utils/skill-lexicon-highlight'
import { matchDomainSearch, poolSummary } from '@/utils/skill-lexicon'
import { useAdminBasePath, adminPath } from '@/components/admin/use-admin-base-path'
import { PluginCanvasLaunchers } from '@/plugin/canvases'

type CatalogTab = 'domains' | 'proposals' | 'import'

function mutationError(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

function resolveSkillId(skills: Skill[], name: string): string | undefined {
  const key = name.trim().toLowerCase()
  return skills.find((skill) => skill.name.trim().toLowerCase() === key)?.id
}

function PublicationControl({
  kind,
  id,
  name,
  status,
}: {
  kind: 'domain' | 'skill'
  id: string
  name: string
  status: PublicationStatus | undefined
}) {
  const mutation = useSetSkillPublicationStatus()
  if (!status) return null
  const next: PublicationStatus = status === 'published' ? 'draft' : 'published'

  return (
    <>
      <Badge variant={status === 'published' ? 'successSoft' : 'warningSoft'}>
        {status === 'published' ? 'Published' : 'Draft'}
      </Badge>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={mutation.isPending}
        aria-label={`${next === 'published' ? 'Publish' : 'Move to draft'} ${name}`}
        onClick={() =>
          mutation.mutate(
            { kind, id, publicationStatus: next },
            {
              onError: (error) => toast.error(mutationError(error, 'Could not change publication status')),
            },
          )
        }
      >
        {next === 'published' ? 'Publish' : 'Move to draft'}
      </Button>
    </>
  )
}

function PoolLine({ rollup }: { rollup: SkillPoolRollup }) {
  const summary = poolSummary(rollup)
  return summary ? <p className="text-sm text-muted-foreground">Talent pool: {summary}</p> : null
}

function AliasChips({
  skillId,
  skillName,
  aliases,
}: {
  skillId: string
  skillName: string
  aliases: string[]
}) {
  const [aliasText, setAliasText] = useState('')
  const [composerOpen, setComposerOpen] = useState(false)
  const addAlias = useAddSkillAlias()
  const removeAlias = useRemoveSkillAlias()

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {aliases.length === 0 ? (
          <span className="text-sm text-muted-foreground">No aliases</span>
        ) : (
          aliases.map((alias) => (
            <Badge key={alias} variant="secondary" className="gap-1 pr-1">
              {alias}
              <button
                type="button"
                className="rounded-sm p-0.5 hover:bg-muted"
                aria-label={`Remove alias ${alias}`}
                disabled={removeAlias.isPending}
                onClick={() =>
                  removeAlias.mutate(alias, {
                    onSuccess: () => toast.success(`Removed ${alias}`),
                    onError: (error) => toast.error(mutationError(error, 'Could not remove alias')),
                  })
                }
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </Badge>
          ))
        )}
        {!composerOpen ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => setComposerOpen(true)}>
            Add alias
          </Button>
        ) : null}
      </div>
      {composerOpen ? (
        <div className="flex max-w-md flex-wrap items-end gap-2">
          <SkillNameComposer
            id={`alias-${skillId}`}
            value={aliasText}
            onValueChange={setAliasText}
            excludeNames={aliases}
            enableSuggestions={false}
            placeholder={`Alias for ${skillName}`}
            onCommit={(name) => {
              addAlias.mutate(
                { skillId, alias: name },
                {
                  onSuccess: () => {
                    setAliasText('')
                    setComposerOpen(false)
                    toast.success('Alias added')
                  },
                  onError: (error) => toast.error(mutationError(error, 'Could not add alias')),
                },
              )
            }}
            onCancel={() => {
              setAliasText('')
              setComposerOpen(false)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

function SkillRow({ skill }: { skill: SkillDomainSkill }) {
  const approve = useApproveSkill()

  return (
    <li>
      <ListCard
        embedded
        agentHighlight={`skill:${skill.id}`}
        className="rounded-md px-2 py-2"
        header={
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{skill.name}</span>
            {skill.status ? (
              <Badge variant={skill.status === 'approved' ? 'successSoft' : 'secondary'}>{skill.status}</Badge>
            ) : null}
            <PublicationControl kind="skill" id={skill.id} name={skill.name} status={skill.publication_status} />
            {skill.status === 'crowdsourced' ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={approve.isPending}
                onClick={() =>
                  approve.mutate(skill.id, {
                    onSuccess: () => toast.success(`Approved ${skill.name}`),
                    onError: (error) => toast.error(mutationError(error, 'Could not approve skill')),
                  })
                }
              >
                Approve
              </Button>
            ) : null}
            <PluginCanvasLaunchers entity={{ kind: 'skill', id: skill.id }} />
          </div>
        }
        body={
          <div className="space-y-2">
            <AliasChips skillId={skill.id} skillName={skill.name} aliases={skill.aliases ?? []} />
            <PoolLine rollup={skill} />
          </div>
        }
      />
    </li>
  )
}

function skillCountLabel(count: number) {
  return `${count} ${count === 1 ? 'skill' : 'skills'}`
}

function DomainRow({
  domain,
  filter,
  open,
  onOpenChange,
}: {
  domain: SkillDomainSummary
  filter: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const listedSkills = domain.skills
  const { data: detail, isLoading } = useSkillDomain(open && !listedSkills ? domain.id : undefined)
  const skills = listedSkills ?? detail?.skills
  const filteredSkills = useMemo(
    () => matchDomainSearch({ label: domain.label, skills }, filter)?.skills,
    [domain.label, filter, skills],
  )
  const narrowed = Boolean(filter.trim() && filteredSkills && filteredSkills.length < domain.skill_count)

  return (
    <Collapsible asChild open={open} onOpenChange={onOpenChange}>
      <li data-agent-highlight={`domain:${domain.id}`}>
        <div className="flex items-center gap-2 px-3 py-2">
          <CollapsibleTrigger className="group flex min-w-0 flex-1 items-center gap-2 rounded-sm py-1 text-left text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            <ChevronRight
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out group-data-[state=open]:rotate-90 motion-reduce:transition-none"
              aria-hidden
            />
            <span className="truncate font-medium">{domain.label}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {narrowed ? `${filteredSkills?.length} of ${skillCountLabel(domain.skill_count)}` : skillCountLabel(domain.skill_count)}
            </span>
          </CollapsibleTrigger>
          <PublicationControl
            kind="domain"
            id={domain.id}
            name={domain.label}
            status={domain.publication_status}
          />
          <PluginCanvasLaunchers entity={{ kind: 'skill-domain', id: domain.id }} />
        </div>
        <CollapsibleContent className="space-y-3 border-t px-3 py-3">
          <PoolLine rollup={domain} />
          {!skills && isLoading ? (
            <p className="text-sm text-muted-foreground" role="status">
              Loading skills…
            </p>
          ) : filteredSkills?.length ? (
            <ul className="space-y-1">
              {filteredSkills.map((skill) => (
                <SkillRow key={skill.id} skill={skill} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No skills in this domain.</p>
          )}
        </CollapsibleContent>
      </li>
    </Collapsible>
  )
}

function CreateDomainDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [label, setLabel] = useState('')
  const createDomain = useCreateSkillDomain()

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setLabel('')
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create domain</DialogTitle>
          <DialogDescription>Adds a draft skill domain to this deployment catalog.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="lexicon-domain-label">Domain label</Label>
          <Input
            id="lexicon-domain-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="e.g. Data & analytics"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!label.trim() || createDomain.isPending}
            onClick={() =>
              createDomain.mutate(
                { label: label.trim() },
                {
                  onSuccess: () => {
                    setLabel('')
                    onOpenChange(false)
                    toast.success('Domain created (draft)')
                  },
                  onError: (error) => toast.error(mutationError(error, 'Could not create domain')),
                },
              )
            }
          >
            Create domain
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CreateSkillDialog({
  open,
  onOpenChange,
  domains,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  domains: SkillDomainSummary[]
}) {
  const [name, setName] = useState('')
  const [domainId, setDomainId] = useState('')
  const createSkill = useCreateApprovedSkill()
  const addMembership = useAddDomainMembership()

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setName('')
          setDomainId('')
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create skill</DialogTitle>
          <DialogDescription>
            Create an approved skill, optionally adding it to a domain. Pick domain membership from the
            catalog — never paste UUIDs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lexicon-skill-name">Skill name</Label>
            <Input
              id="lexicon-skill-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Approved skill name"
            />
          </div>
          <div className="space-y-2">
            <Label>Add to domain (optional)</Label>
            <Select value={domainId || '__none__'} onValueChange={(value) => setDomainId(value === '__none__' ? '' : value)}>
              <SelectTrigger aria-label="Domain for new skill">
                <SelectValue placeholder="No domain yet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No domain yet</SelectItem>
                {domains.map((domain) => (
                  <SelectItem key={domain.id} value={domain.id}>
                    {domain.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || createSkill.isPending || addMembership.isPending}
            onClick={() => {
              const trimmed = name.trim()
              createSkill.mutate(trimmed, {
                onSuccess: (skill) => {
                  const finish = () => {
                    setName('')
                    setDomainId('')
                    onOpenChange(false)
                    toast.success(`Created ${skill.name}`)
                  }
                  if (!domainId) {
                    finish()
                    return
                  }
                  addMembership.mutate(
                    { domainId, skillId: skill.id },
                    {
                      onSuccess: finish,
                      onError: (error) =>
                        toast.error(mutationError(error, 'Skill created but membership failed')),
                    },
                  )
                },
                onError: (error) => toast.error(mutationError(error, 'Could not create skill')),
              })
            }}
          >
            Create skill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AddMembershipDialog({
  open,
  onOpenChange,
  domains,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  domains: SkillDomainSummary[]
}) {
  const { skills } = useSkills()
  const [domainId, setDomainId] = useState('')
  const [skillQuery, setSkillQuery] = useState('')
  const addMembership = useAddDomainMembership()

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setDomainId('')
          setSkillQuery('')
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add skill to domain</DialogTitle>
          <DialogDescription>Choose a domain and pick a skill by name.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Domain</Label>
            <Select value={domainId} onValueChange={setDomainId}>
              <SelectTrigger aria-label="Domain for membership">
                <SelectValue placeholder="Select domain…" />
              </SelectTrigger>
              <SelectContent>
                {domains.map((domain) => (
                  <SelectItem key={domain.id} value={domain.id}>
                    {domain.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Skill</Label>
            <SkillNameComposer
              id="lexicon-membership-skill"
              value={skillQuery}
              onValueChange={setSkillQuery}
              excludeNames={[]}
              placeholder="Search catalog skills…"
              onCommit={(skillName) => setSkillQuery(skillName)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!domainId || !skillQuery.trim() || addMembership.isPending}
            onClick={() => {
              const skillId = resolveSkillId(skills, skillQuery)
              if (!skillId) {
                toast.error('Pick an existing catalog skill')
                return
              }
              addMembership.mutate(
                { domainId, skillId },
                {
                  onSuccess: () => {
                    setDomainId('')
                    setSkillQuery('')
                    onOpenChange(false)
                    toast.success('Membership added')
                  },
                  onError: (error) => toast.error(mutationError(error, 'Could not add membership')),
                },
              )
            }}
          >
            Add to domain
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MergeSkillsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { skills } = useSkills()
  const [duplicateQuery, setDuplicateQuery] = useState('')
  const [canonicalQuery, setCanonicalQuery] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const mergeSkills = useMergeSkills()

  const duplicateId = resolveSkillId(skills, duplicateQuery)
  const canonicalId = resolveSkillId(skills, canonicalQuery)
  const canPrepare = Boolean(duplicateId && canonicalId && duplicateId !== canonicalId)

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setDuplicateQuery('')
            setCanonicalQuery('')
          }
          onOpenChange(next)
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Merge skills</DialogTitle>
            <DialogDescription>
              Move possessions and aliases onto the canonical skill, then delete the duplicate. Pick
              skills by name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Duplicate (will be removed)</Label>
              <SkillNameComposer
                id="lexicon-merge-duplicate"
                value={duplicateQuery}
                onValueChange={setDuplicateQuery}
                excludeNames={canonicalQuery ? [canonicalQuery] : []}
                placeholder="Search duplicate skill…"
                onCommit={(skillName) => setDuplicateQuery(skillName)}
              />
            </div>
            <div className="space-y-2">
              <Label>Canonical (kept)</Label>
              <SkillNameComposer
                id="lexicon-merge-canonical"
                value={canonicalQuery}
                onValueChange={setCanonicalQuery}
                excludeNames={duplicateQuery ? [duplicateQuery] : []}
                placeholder="Search canonical skill…"
                onCommit={(skillName) => setCanonicalQuery(skillName)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!canPrepare || mergeSkills.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              Review merge…
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              Merge “{duplicateQuery}” into “{canonicalQuery}”. Possessions and aliases move to the
              canonical skill; the duplicate is deleted. This cannot be undone from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canPrepare || mergeSkills.isPending}
              onClick={(event) => {
                event.preventDefault()
                if (!duplicateId || !canonicalId) return
                mergeSkills.mutate(
                  { duplicateSkillId: duplicateId, canonicalSkillId: canonicalId },
                  {
                    onSuccess: () => {
                      setConfirmOpen(false)
                      setDuplicateQuery('')
                      setCanonicalQuery('')
                      onOpenChange(false)
                      toast.success('Skills merged')
                    },
                    onError: (error) => toast.error(mutationError(error, 'Could not merge skills')),
                  },
                )
              }}
            >
              Merge skills
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function ProposalsPanel() {
  const { data: proposals = [], isLoading, error } = useLexiconProposals()
  const applyProposal = useApplyLexiconProposal()
  const dismissProposal = useDismissLexiconProposal()

  if (error) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : 'Failed to load proposals'}
      </p>
    )
  }
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading proposals…
      </p>
    )
  }
  if (proposals.length === 0) {
    return <p className="text-sm text-muted-foreground">No pending proposals.</p>
  }

  return (
    <ul className="space-y-2">
      {proposals.map((proposal) => (
        <li
          key={proposal.id}
          className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm"
        >
          <Badge variant="secondary">{proposal.kind}</Badge>
          <span className="min-w-0 flex-1">
            {proposal.alias_text || proposal.skill_name || '—'}
            {proposal.canonical_name ? ` → ${proposal.canonical_name}` : ''}
          </span>
          {proposal.confidence != null ? (
            <span className="text-muted-foreground">{Math.round(proposal.confidence * 100)}%</span>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={applyProposal.isPending}
            onClick={() =>
              applyProposal.mutate(proposal.id, {
                onSuccess: () => toast.success('Proposal applied'),
                onError: (error) => toast.error(mutationError(error, 'Could not apply proposal')),
              })
            }
          >
            Apply
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={dismissProposal.isPending}
            onClick={() =>
              dismissProposal.mutate(proposal.id, {
                onSuccess: () => toast.success('Proposal dismissed'),
                onError: (error) => toast.error(mutationError(error, 'Could not dismiss proposal')),
              })
            }
          >
            Dismiss
          </Button>
        </li>
      ))}
    </ul>
  )
}

function ImportExportPanel({
  csvText,
  setCsvText,
}: {
  csvText: string
  setCsvText: (value: string) => void
}) {
  const [previewSummary, setPreviewSummary] = useState('')
  const [previewOk, setPreviewOk] = useState<boolean | null>(null)
  const importPreview = useCatalogImportPreview()
  const importApply = useCatalogImportApply()

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Staged CSV import: paste or load export → dry-run summary → Apply. Columns: row_type
        (domain|skill|alias), domain_key, key, label, parent_key, sort_order, name, status,
        publication_status, alias, canonical_name. Agent tools import_preview / import_apply /
        export_catalog remain as fallback.
      </p>
      <Textarea
        value={csvText}
        onChange={(event) => {
          setCsvText(event.target.value)
          setPreviewSummary('')
          setPreviewOk(null)
        }}
        rows={12}
        placeholder="Paste catalog CSV…"
        aria-label="Catalog CSV"
        className="font-mono text-xs"
      />
      {previewSummary ? (
        <p
          className={`rounded-md border px-3 py-2 text-sm ${
            previewOk === false ? 'border-destructive/40 text-destructive' : 'bg-muted/40'
          }`}
          role="status"
        >
          {previewSummary}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!csvText.trim() || importPreview.isPending}
          onClick={() =>
            importPreview.mutate(
              { csv: csvText },
              {
                onSuccess: (preview) => {
                  setPreviewOk(preview.ok)
                  setPreviewSummary(
                    preview.ok
                      ? `Dry-run OK: ${preview.change_count} change(s)`
                      : `Dry-run blocked: ${preview.conflict_count} conflict(s)`,
                  )
                },
                onError: (error) => toast.error(mutationError(error, 'Preview failed')),
              },
            )
          }
        >
          Dry-run
        </Button>
        <Button
          type="button"
          disabled={!csvText.trim() || previewOk !== true || importApply.isPending}
          onClick={() =>
            importApply.mutate(
              { csv: csvText },
              {
                onSuccess: (result) => {
                  setPreviewSummary(`Applied ${result.applied_count} row(s)`)
                  toast.success(`Applied ${result.applied_count} row(s)`)
                },
                onError: (error) => toast.error(mutationError(error, 'Apply failed')),
              },
            )
          }
        >
          Apply import
        </Button>
      </div>
    </div>
  )
}

export function SkillsCatalogPage() {
  const basePath = useAdminBasePath()
  const location = useLocation()
  const { data: domains = [], isLoading, error } = useSkillDomains('employment')
  const { data: proposals = [] } = useLexiconProposals()
  const exportCatalog = useCatalogExport()
  const [filter, setFilter] = useState('')
  const [tab, setTab] = useState<CatalogTab>('domains')
  const [csvText, setCsvText] = useState('')
  const [createDomainOpen, setCreateDomainOpen] = useState(false)
  const [createSkillOpen, setCreateSkillOpen] = useState(false)
  const [membershipOpen, setMembershipOpen] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)

  const [browseOpenIds, setBrowseOpenIds] = useState<ReadonlySet<string>>(() => new Set())
  const [searchOpen, setSearchOpen] = useState<{ query: string; ids: ReadonlySet<string> }>({
    query: '',
    ids: new Set(),
  })

  const highlight = new URLSearchParams(location.search).get(AGENT_HIGHLIGHT_PARAM)
  const autoOpenId = domainIdForHighlight(domains, highlight)

  useEffect(() => {
    if (autoOpenId) setBrowseOpenIds((current) => new Set(current).add(autoOpenId))
  }, [autoOpenId])

  const searching = Boolean(filter.trim())
  const visibleDomains = useMemo(
    () => (searching ? domains.filter((domain) => matchDomainSearch(domain, filter)) : domains),
    [domains, filter, searching],
  )
  // Each new query opens every matching domain; toggles then apply until the query changes.
  const openIds = !searching
    ? browseOpenIds
    : searchOpen.query === filter
      ? searchOpen.ids
      : new Set(visibleDomains.map((domain) => domain.id))
  const setOpenIds = (ids: ReadonlySet<string>) => {
    if (searching) setSearchOpen({ query: filter, ids })
    else setBrowseOpenIds(ids)
  }
  const toggleDomain = (id: string, open: boolean) => {
    const next = new Set(openIds)
    if (open) next.add(id)
    else next.delete(id)
    setOpenIds(next)
  }
  const allOpen = visibleDomains.length > 0 && visibleDomains.every((domain) => openIds.has(domain.id))
  const noneOpen = !visibleDomains.some((domain) => openIds.has(domain.id))

  const header = getPageHeader(
    adminPath(basePath, 'skills-catalog'),
    'Skill Lexicon',
    'Manage approved skills, aliases, domains, publication, proposals, and CSV import/export. Agent chat remains a fallback for bulk natural-language edits.',
  )

  const tabItems = useMemo(
    () => [
      { value: 'domains' as const, label: 'Domains' },
      {
        value: 'proposals' as const,
        label: 'Proposals',
        badge: proposals.length ? <TabCountBadge count={proposals.length} /> : undefined,
      },
      { value: 'import' as const, label: 'Import' },
    ],
    [proposals.length],
  )

  return (
    <PageContainer>
      <PageHeaderStack compact>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={header.title}
          description={header.description}
          actions={
            <PageHeaderActions
              secondary={
                <>
                  <PageHeaderActionSecondary type="button" onClick={() => setMergeOpen(true)}>
                    <GitMerge className="h-4 w-4" aria-hidden />
                    Merge
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary
                    type="button"
                    disabled={exportCatalog.isPending}
                    onClick={() =>
                      exportCatalog.mutate(
                        { domainKey: 'employment' },
                        {
                          onSuccess: (result) => {
                            setCsvText(result.csv)
                            setTab('import')
                            toast.success('Exported catalog into Import')
                          },
                          onError: (error) => toast.error(mutationError(error, 'Export failed')),
                        },
                      )
                    }
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    Export
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary type="button" onClick={() => setTab('import')}>
                    <Upload className="h-4 w-4" aria-hidden />
                    Import
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary type="button" onClick={() => setMembershipOpen(true)}>
                    Add to domain
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary type="button" onClick={() => setCreateDomainOpen(true)}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Create domain
                  </PageHeaderActionSecondary>
                </>
              }
              primary={
                <PageHeaderActionPrimary type="button" onClick={() => setCreateSkillOpen(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Create skill
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        <PageLineTabs
          value={tab}
          onValueChange={(value) => setTab(value as CatalogTab)}
          ariaLabel="Skill lexicon sections"
          items={tabItems}
        />

        {tab === 'domains' ? (
          <ListBrowseToolbar
            searchId="skill-lexicon-search"
            searchPlaceholder="Search domains, skills, aliases…"
            searchValue={filter}
            onSearchChange={setFilter}
            trailingActions={
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="toolbarSm"
                  disabled={allOpen}
                  onClick={() => setOpenIds(new Set(visibleDomains.map((domain) => domain.id)))}
                >
                  <ChevronsUpDown className="h-4 w-4" aria-hidden />
                  Expand all
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="toolbarSm"
                  disabled={noneOpen}
                  onClick={() => setOpenIds(new Set())}
                >
                  <ChevronsDownUp className="h-4 w-4" aria-hidden />
                  Collapse all
                </Button>
              </>
            }
          />
        ) : null}
      </PageHeaderStack>

      {tab === 'domains' ? (
        <Card>
          <CardHeader>
            <CardTitle>Skill domains</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Domains reflect the bootstrapped catalog for this deployment. Edit rubric focus areas in{' '}
              <Link to={adminPath(basePath, 'evaluation-rubrics')} className="underline underline-offset-2">
                Analysis rubric
              </Link>
              .
            </p>
            {error ? (
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : 'Failed to load catalog'}
              </p>
            ) : isLoading ? (
              <p className="text-sm text-muted-foreground" role="status">
                Loading catalog…
              </p>
            ) : domains.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No domains found. Run bootstrap_skill_domains for this tenant.
              </p>
            ) : visibleDomains.length === 0 ? (
              <p className="text-sm text-muted-foreground">No domains, skills or aliases match “{filter.trim()}”.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {visibleDomains.map((domain) => (
                  <DomainRow
                    key={domain.id}
                    domain={domain}
                    filter={filter}
                    open={openIds.has(domain.id)}
                    onOpenChange={(open) => toggleDomain(domain.id, open)}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      {tab === 'proposals' ? (
        <Card>
          <CardHeader>
            <CardTitle>Pending proposals</CardTitle>
          </CardHeader>
          <CardContent>
            <ProposalsPanel />
          </CardContent>
        </Card>
      ) : null}

      {tab === 'import' ? (
        <Card>
          <CardHeader>
            <CardTitle>CSV import</CardTitle>
          </CardHeader>
          <CardContent>
            <ImportExportPanel csvText={csvText} setCsvText={setCsvText} />
          </CardContent>
        </Card>
      ) : null}

      <CreateDomainDialog open={createDomainOpen} onOpenChange={setCreateDomainOpen} />
      <CreateSkillDialog open={createSkillOpen} onOpenChange={setCreateSkillOpen} domains={domains} />
      <AddMembershipDialog open={membershipOpen} onOpenChange={setMembershipOpen} domains={domains} />
      <MergeSkillsDialog open={mergeOpen} onOpenChange={setMergeOpen} />
    </PageContainer>
  )
}
